import { useState, useEffect, useCallback } from "react";
import {
  ChevronLeft,
  Send,
  Search,
  Calendar,
  CheckCircle,
  ArrowUp,
  MessageSquare,
  X,
  Play,
  Pause,
  ChevronRight,
  RotateCcw,
} from "lucide-react";

const STEPS = [
  {
    id: "create-task",
    title: "Crea un Compito",
    subtitle: "Inserisci il numero camera e scrivi il messaggio",
  },
  {
    id: "task-created",
    title: "Compito Creato",
    subtitle: "Il compito appare con la pill della camera in evidenza",
  },
  {
    id: "add-comment",
    title: "Aggiungi una Nota",
    subtitle: "Clicca sulla pill per aggiungere aggiornamenti senza chiudere",
  },
  {
    id: "close-task",
    title: "Chiudi il Compito",
    subtitle: "Conferma la chiusura con una nota opzionale",
  },
  {
    id: "search-past",
    title: "Cerca nei Giorni Passati",
    subtitle: "Usa la ricerca o il calendario per trovare compiti precedenti",
  },
  {
    id: "future-task",
    title: "Compito Programmato",
    subtitle: "Seleziona una data futura dal calendario per programmare un compito",
  },
];

const LandingDemo = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [typing, setTyping] = useState("");
  const [typingField, setTypingField] = useState("");

  const goNext = useCallback(() => {
    setCurrentStep((prev) => (prev + 1) % STEPS.length);
  }, []);

  const goPrev = () => {
    setCurrentStep((prev) => (prev - 1 + STEPS.length) % STEPS.length);
  };

  const restart = () => {
    setCurrentStep(0);
    setIsPlaying(true);
  };

  // Auto-advance
  useEffect(() => {
    if (!isPlaying) return;
    const timer = setTimeout(goNext, 4000);
    return () => clearTimeout(timer);
  }, [currentStep, isPlaying, goNext]);

  // Typing animation per step
  useEffect(() => {
    setTyping("");
    setTypingField("");
    const step = STEPS[currentStep];

    if (step.id === "create-task") {
      const roomText = "412";
      const msgText = "Rubinetto bagno gocciola, ospite segnala perdita";
      let i = 0;
      setTypingField("room");
      const t1 = setInterval(() => {
        if (i < roomText.length) {
          setTyping(roomText.slice(0, i + 1));
          i++;
        } else {
          clearInterval(t1);
          setTimeout(() => {
            setTypingField("message");
            setTyping("");
            let j = 0;
            const t2 = setInterval(() => {
              if (j < msgText.length) {
                setTyping(msgText.slice(0, j + 1));
                j++;
              } else {
                clearInterval(t2);
              }
            }, 35);
          }, 400);
        }
      }, 80);
      return () => clearInterval(t1);
    }
  }, [currentStep]);

  const step = STEPS[currentStep];

  return (
    <div className="w-full">
      {/* Step indicator + controls */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-1.5">
          {STEPS.map((s, i) => (
            <button
              key={s.id}
              onClick={() => { setCurrentStep(i); setIsPlaying(false); }}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === currentStep
                  ? "w-8 bg-amber-500"
                  : i < currentStep
                  ? "w-3 bg-amber-300"
                  : "w-3 bg-stone-600"
              }`}
              aria-label={`Step ${i + 1}`}
            />
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={goPrev}
            className="w-8 h-8 rounded-full bg-stone-700 hover:bg-stone-600 flex items-center justify-center text-stone-300 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => setIsPlaying(!isPlaying)}
            className="w-8 h-8 rounded-full bg-amber-500/20 hover:bg-amber-500/30 flex items-center justify-center text-amber-400 transition-colors"
            data-testid="demo-play-pause"
          >
            {isPlaying ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5 ml-0.5" />}
          </button>
          <button
            onClick={goNext}
            className="w-8 h-8 rounded-full bg-stone-700 hover:bg-stone-600 flex items-center justify-center text-stone-300 transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <button
            onClick={restart}
            className="w-8 h-8 rounded-full bg-stone-700 hover:bg-stone-600 flex items-center justify-center text-stone-400 transition-colors"
          >
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Step title */}
      <div className="mb-4 min-h-[52px]">
        <p className="text-xs font-medium text-amber-400 uppercase tracking-wider mb-1">
          Passo {currentStep + 1} di {STEPS.length}
        </p>
        <h4 className="font-outfit text-lg font-semibold text-white">{step.title}</h4>
        <p className="text-sm text-stone-400">{step.subtitle}</p>
      </div>

      {/* Phone mockup */}
      <div className="relative mx-auto" style={{ maxWidth: 340 }}>
        {/* Phone frame */}
        <div className="rounded-[2rem] border-[6px] border-stone-700 bg-stone-50 overflow-hidden shadow-2xl shadow-black/30">
          {/* Status bar */}
          <div className="bg-stone-50 px-5 pt-2 pb-1 flex items-center justify-between">
            <span className="text-[10px] font-semibold text-stone-700">9:41</span>
            <div className="flex items-center gap-1">
              <div className="w-3.5 h-2 rounded-sm border border-stone-400 relative">
                <div className="absolute inset-0.5 bg-stone-700 rounded-[1px]" style={{ width: "70%" }} />
              </div>
            </div>
          </div>

          {/* App content area */}
          <div className="h-[520px] overflow-hidden relative bg-stone-50">
            {/* ===== STEP: CREATE TASK ===== */}
            <div className={`absolute inset-0 transition-all duration-500 ${step.id === "create-task" ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8 pointer-events-none"}`}>
              <PhoneHeader title="Manutenzione - Hotel" subtitle="Oggi" />
              <div className="px-3 py-3 flex-1">
                <Message user="Marco R." time="08:15" content="Buongiorno, lavandino camera #301 intasato" hasTask taskNum="301" taskStatus="open" />
              </div>
              {/* Input area */}
              <div className="absolute bottom-0 inset-x-0 bg-white border-t border-stone-200 p-3">
                <div className="mb-2">
                  <div className={`h-9 rounded-xl bg-amber-50 border px-3 flex items-center font-mono text-sm ${typingField === "room" ? "border-amber-400" : "border-amber-200"}`}>
                    <span className="text-amber-800">{typingField === "room" ? typing : "412"}</span>
                    {typingField === "room" && <span className="w-0.5 h-4 bg-amber-500 ml-0.5 animate-pulse" />}
                    {typingField !== "room" && !typing && <span className="text-amber-300 text-xs">N° Camera</span>}
                  </div>
                </div>
                <div className="flex gap-2">
                  <div className={`flex-1 h-9 rounded-full bg-stone-100 px-3 flex items-center text-sm ${typingField === "message" ? "ring-2 ring-stone-300" : ""}`}>
                    <span className="text-stone-700 truncate">{typingField === "message" ? typing : ""}</span>
                    {typingField === "message" && <span className="w-0.5 h-4 bg-stone-400 ml-0.5 animate-pulse" />}
                    {typingField !== "message" && <span className="text-stone-400 text-xs">Scrivi un messaggio...</span>}
                  </div>
                  <div className="w-9 h-9 rounded-full bg-stone-900 flex items-center justify-center">
                    <Send className="w-4 h-4 text-white" />
                  </div>
                </div>
              </div>
            </div>

            {/* ===== STEP: TASK CREATED ===== */}
            <div className={`absolute inset-0 transition-all duration-500 ${step.id === "task-created" ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8 pointer-events-none"}`}>
              <PhoneHeader title="Manutenzione - Hotel" subtitle="Oggi" />
              <div className="px-3 py-3 space-y-3">
                <Message user="Marco R." time="08:15" content="Buongiorno, lavandino camera #301 intasato" hasTask taskNum="301" taskStatus="open" />
                <Message user="Tu" time="08:22" content="Rubinetto bagno gocciola, ospite segnala perdita" hasTask taskNum="412" taskStatus="open" isNew />
              </div>
              {/* Highlight arrow */}
              <div className="absolute right-6 top-[240px] flex items-center gap-2 animate-bounce">
                <span className="text-xs font-medium text-amber-600 bg-amber-50 px-2 py-1 rounded-lg border border-amber-200 shadow-sm whitespace-nowrap">
                  Compito creato!
                </span>
              </div>
            </div>

            {/* ===== STEP: ADD COMMENT ===== */}
            <div className={`absolute inset-0 transition-all duration-500 ${step.id === "add-comment" ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8 pointer-events-none"}`}>
              <PhoneHeader title="Manutenzione - Hotel" subtitle="Oggi" />
              <div className="px-3 py-3 space-y-3">
                <Message user="Marco R." time="08:15" content="Lavandino camera #301 intasato" hasTask taskNum="301" taskStatus="open" />
                <div>
                  <Message user="Tu" time="08:22" content="Rubinetto bagno gocciola" hasTask taskNum="412" taskStatus="open" />
                  {/* Comment popup */}
                  <div className="ml-11 mt-1 bg-white rounded-xl border border-stone-200 shadow-lg p-3 animate-in">
                    <p className="text-[10px] font-medium text-stone-500 uppercase mb-2">Aggiungi Nota</p>
                    <div className="bg-stone-50 rounded-lg p-2 text-xs text-stone-600 border border-stone-200 mb-2">
                      Idraulico chiamato, arriva alle 14:00
                    </div>
                    <div className="flex justify-end">
                      <div className="px-3 py-1.5 rounded-lg bg-amber-500 text-white text-xs font-medium">
                        Aggiungi Nota
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ===== STEP: CLOSE TASK ===== */}
            <div className={`absolute inset-0 transition-all duration-500 ${step.id === "close-task" ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8 pointer-events-none"}`}>
              <PhoneHeader title="Manutenzione - Hotel" subtitle="Oggi" />
              <div className="px-3 py-3 space-y-3">
                <Message user="Marco R." time="08:15" content="Lavandino camera #301 intasato" hasTask taskNum="301" taskStatus="open" />
                {/* Close modal overlay */}
                <div className="bg-white rounded-2xl border border-stone-200 shadow-xl p-4 mx-1">
                  <div className="text-center mb-3">
                    <p className="font-outfit font-semibold text-stone-900">Chiudi Passaggio Consegne</p>
                    <p className="text-xs text-stone-500 mt-1">
                      Compito <span className="font-mono font-bold text-amber-600">#412</span>
                    </p>
                  </div>
                  <div className="bg-stone-50 rounded-lg p-2.5 text-xs text-stone-600 border border-stone-200 mb-3">
                    Riparato, sostituita guarnizione rubinetto
                  </div>
                  <div className="flex gap-2">
                    <div className="flex-1 py-2 rounded-xl border border-stone-200 text-center text-xs text-stone-500">
                      Annulla
                    </div>
                    <div className="flex-1 py-2 rounded-xl bg-emerald-600 text-center text-xs text-white font-medium flex items-center justify-center gap-1">
                      <CheckCircle className="w-3 h-3" />
                      Conferma
                    </div>
                  </div>
                </div>
                {/* Closed task below */}
                <Message user="Tu" time="08:22" content="Rubinetto bagno gocciola" hasTask taskNum="412" taskStatus="closed" />
              </div>
            </div>

            {/* ===== STEP: SEARCH PAST ===== */}
            <div className={`absolute inset-0 transition-all duration-500 ${step.id === "search-past" ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8 pointer-events-none"}`}>
              <PhoneHeader title="Manutenzione - Hotel" subtitle="22 Agosto 2026" showSearch />
              {/* Search bar */}
              <div className="px-3 py-2 flex gap-2">
                <div className="flex-1 h-8 rounded-full bg-stone-100 border border-stone-200 px-3 flex items-center">
                  <Search className="w-3 h-3 text-stone-400 mr-1.5" />
                  <span className="text-xs text-stone-700">rubinetto</span>
                </div>
                <div className="px-3 h-8 rounded-full bg-stone-900 text-white text-xs flex items-center font-medium">
                  Cerca
                </div>
              </div>
              {/* Results */}
              <div className="px-3 py-2 space-y-3">
                <div className="flex items-center gap-1 text-amber-600 text-[10px] mb-1">
                  <ArrowUp className="w-3 h-3" />
                  <span>Portato avanti dai giorni precedenti</span>
                </div>
                <Message user="Tu" time="08:22" content="Rubinetto bagno gocciola, ospite segnala perdita" hasTask taskNum="412" taskStatus="closed" />
                <p className="text-center text-[10px] text-stone-400 pt-2">
                  Risultati dal 22 Agosto 2026
                </p>
              </div>
              {/* Calendar hint */}
              <div className="absolute bottom-20 right-3 flex items-center gap-2">
                <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-2 py-1 rounded-lg border border-amber-200 shadow-sm">
                  Usa il calendario per navigare
                </span>
              </div>
            </div>

            {/* ===== STEP: FUTURE TASK ===== */}
            <div className={`absolute inset-0 transition-all duration-500 ${step.id === "future-task" ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8 pointer-events-none"}`}>
              <PhoneHeader title="Manutenzione - Hotel" subtitle="30 Agosto 2026" />
              {/* Calendar popover */}
              <div className="absolute top-12 right-2 bg-white rounded-xl border border-stone-200 shadow-xl p-3 z-10 w-[200px]">
                <div className="text-center mb-2">
                  <p className="text-xs font-semibold text-stone-900">Agosto 2026</p>
                </div>
                <div className="grid grid-cols-7 gap-1 text-[9px] text-center">
                  {["L", "M", "M", "G", "V", "S", "D"].map((d, i) => (
                    <span key={i} className="text-stone-400 font-medium">{d}</span>
                  ))}
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                    <span
                      key={d}
                      className={`w-5 h-5 flex items-center justify-center rounded-full ${
                        d === 30
                          ? "bg-amber-500 text-white font-bold"
                          : d === 27
                          ? "bg-stone-200 text-stone-900 font-bold"
                          : "text-stone-600"
                      }`}
                    >
                      {d}
                    </span>
                  ))}
                </div>
                <div className="mt-2 pt-2 border-t border-stone-100 text-center">
                  <span className="text-[10px] text-amber-600 font-medium">30 selezionato</span>
                </div>
              </div>
              <div className="px-3 pt-4 pb-3 mt-[180px]">
                <div className="text-center py-6 text-stone-400">
                  <Calendar className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  <p className="text-xs">Nessun messaggio per il 30 Agosto</p>
                  <p className="text-[10px] text-stone-300 mt-1">I compiti creati oggi appariranno questa data</p>
                </div>
              </div>
              {/* Input with future date indicator */}
              <div className="absolute bottom-0 inset-x-0 bg-white border-t border-stone-200 p-3">
                <div className="mb-2">
                  <div className="h-9 rounded-xl bg-amber-50 border border-amber-400 px-3 flex items-center font-mono text-sm">
                    <span className="text-amber-800">508</span>
                  </div>
                </div>
                <p className="text-[10px] text-amber-600 mb-2 ml-1 flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  Il compito partirà dal 30 Agosto 2026
                </p>
                <div className="flex gap-2">
                  <div className="flex-1 h-9 rounded-full bg-stone-100 px-3 flex items-center text-sm">
                    <span className="text-stone-700 text-xs truncate">Preparare camera VIP per arrivo gruppo</span>
                  </div>
                  <div className="w-9 h-9 rounded-full bg-stone-900 flex items-center justify-center">
                    <Send className="w-4 h-4 text-white" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Phone home indicator */}
        <div className="flex justify-center mt-2">
          <div className="w-28 h-1 rounded-full bg-stone-600" />
        </div>
      </div>
    </div>
  );
};

/* ===== Sub-components ===== */

const PhoneHeader = ({ title, subtitle, showSearch }) => (
  <div className="bg-stone-50/90 backdrop-blur-sm border-b border-stone-200 px-3 py-2.5 flex items-center gap-2 sticky top-0 z-10">
    <div className="w-7 h-7 rounded-full bg-stone-100 flex items-center justify-center text-stone-500">
      <ChevronLeft className="w-4 h-4" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-semibold text-stone-900 truncate">{title}</p>
      <p className="text-[10px] text-stone-500">{subtitle}</p>
    </div>
    <div className="flex items-center gap-1">
      {showSearch && (
        <div className="w-7 h-7 rounded-full bg-stone-100 flex items-center justify-center text-stone-500">
          <Search className="w-3.5 h-3.5" />
        </div>
      )}
      <div className="w-7 h-7 rounded-full bg-stone-100 flex items-center justify-center text-stone-500">
        <Calendar className="w-3.5 h-3.5" />
      </div>
    </div>
  </div>
);

const Message = ({ user, time, content, hasTask, taskNum, taskStatus, isNew }) => (
  <div className={`flex gap-2 ${isNew ? "animate-in" : ""}`}>
    <div className="w-7 h-7 rounded-full bg-stone-300 flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0 mt-0.5">
      {user.split(" ").map((n) => n[0]).join("").slice(0, 2)}
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-baseline gap-1.5 mb-0.5">
        <span className="text-xs font-semibold text-stone-900">{user}</span>
        <span className="text-[10px] text-stone-400">{time}</span>
      </div>
      {hasTask ? (
        <div className={`rounded-lg border p-2 ${
          taskStatus === "closed"
            ? "border-emerald-200 bg-emerald-50/30"
            : "border-amber-200 bg-amber-50/30"
        }`}>
          <div className="flex items-center gap-1.5 mb-1">
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full font-mono text-[10px] font-bold ${
              taskStatus === "closed"
                ? "bg-emerald-50 text-emerald-700 border border-emerald-200 line-through"
                : "bg-amber-100 text-amber-800 border border-amber-300"
            }`}>
              #{taskNum}
            </span>
            {taskStatus === "closed" && (
              <span className="text-[9px] text-emerald-600 flex items-center gap-0.5">
                <CheckCircle className="w-2.5 h-2.5" />
                Chiuso
              </span>
            )}
          </div>
          <p className="text-xs text-stone-600 leading-relaxed">{content}</p>
          {taskStatus === "closed" && (
            <p className="text-[9px] text-emerald-600 mt-1 italic">
              "Riparato, sostituita guarnizione"
            </p>
          )}
        </div>
      ) : (
        <p className="text-xs text-stone-600">{content}</p>
      )}
    </div>
  </div>
);

export default LandingDemo;
